/*
 * Formatador de data ISO STRING-BASED (FE-3 CP-C — ADR 0026).
 *
 * POR QUE STRING-BASED (e não `new Date`): o wire entrega a data de competência
 * como ISO `YYYY-MM-DD` (sem hora). `new Date("2026-07-01")` é interpretado como
 * MEIA-NOITE UTC; num fuso negativo (Brasil, UTC-3) a leitura local RETROCEDE
 * para `2026-06-30` — ou seja, a data exibida ao operador fica ERRADA em um dia.
 * Numa conciliação bancária (casamento por valor + DATA), exibir o dia errado é
 * um erro FUNCIONAL, não cosmético. Por isso a conversão é puramente TEXTUAL:
 * `split` dos três campos e recomposição, sem nunca instanciar `Date`.
 *
 * Mesmo princípio do `formatarDecimal` (string-based, sem `Number`).
 */

/** Casa estritamente `YYYY-MM-DD` — qualquer outra forma é tratada como crua. */
const PADRAO_ISO = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Casa um datetime ISO `YYYY-MM-DDTHH:MM` (com segundos/fração/`Z`/offset
 * opcionais). Capturamos só data + `HH:MM` — segundos e fuso são descartados na
 * exibição. Qualquer outra forma é tratada como crua.
 */
const PADRAO_ISO_DATETIME =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?$/;

/**
 * Converte uma data ISO `YYYY-MM-DD` para o padrão pt-BR `DD/MM/YYYY`.
 *
 * Entrada: `"2026-07-01"` → Saída: `"01/07/2026"`. Puramente textual (split por
 * `-`), timezone-safe: nunca instancia `Date`, logo nunca há deslocamento de fuso.
 *
 * Defensivo: entrada que NÃO casa `^\d{4}-\d{2}-\d{2}$` (vazia, datetime com hora,
 * formato inesperado) é devolvida CRUA — a UI prefere mostrar o valor original a
 * inventar uma data malformada.
 */
export function formatarDataISO(iso: string): string {
  if (!PADRAO_ISO.test(iso)) {
    return iso;
  }
  const [ano, mes, dia] = iso.split("-");
  return `${dia}/${mes}/${ano}`;
}

/**
 * Converte um datetime ISO `YYYY-MM-DDTHH:MM[:SS][Z|±HH:MM]` para o padrão pt-BR
 * `DD/MM/YYYY HH:MM`.
 *
 * Entrada: `"2026-06-16T10:05:00Z"` → Saída: `"16/06/2026 10:05"`. Puramente
 * TEXTUAL (regex de captura), pela mesma razão de `formatarDataISO`: nunca
 * instanciamos `Date`, logo nunca há deslocamento de fuso que mude o dia exibido
 * na trilha de auditoria. Exibimos a data/hora EXATAMENTE como veio no wire (o
 * backend grava o instante; a UI não reinterpreta fuso).
 *
 * Defensivo: entrada que não casa o padrão (vazia, só data, formato inesperado)
 * é devolvida CRUA — a UI prefere o valor original a inventar um instante.
 */
export function formatarDataHoraISO(iso: string): string {
  const casamento = PADRAO_ISO_DATETIME.exec(iso);
  if (casamento === null) {
    return iso;
  }
  const [, ano, mes, dia, hora, minuto] = casamento;
  return `${dia}/${mes}/${ano} ${hora}:${minuto}`;
}
